import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Icon, MenuItem, Select, Menu,
} from '@material-ui/core';
import { Sort } from '@material-ui/icons';
import { IconButton, Button } from '../../../components';


const styles = {
  actions: {
    display: 'flex',
  },
  actionElement: {
    margin: '0px',
  },
  fullWidth: {
    width: '100%',
  },
};


const Description = forwardRef((props, ref) => {
  const {
    // eslint-disable-next-line react/prop-types
    description, overlayVisible, setOverlayVisible, inMenu,
  } = props;

  if (!description) {
    return null;
  }

  if (inMenu) {
    return (
      <MenuItem ref={ref} onClick={() => setOverlayVisible(!overlayVisible)}>
        Beschreibung
      </MenuItem>
    );
  }

  return (
    <IconButton onClick={() => setOverlayVisible(!overlayVisible)} style={styles.actionElement}>
      <Icon>help</Icon>
    </IconButton>
  );
});

const Sortables = forwardRef((props, ref) => {
  const {
    // eslint-disable-next-line react/prop-types
    sortByAttribute, sortableAttributes, setSortAttribute, useDesktopLayout, inMenu,
  } = props;

  if (!sortableAttributes) {
    return null;
  }

  const sortItems = Object.entries(sortableAttributes).map(([name, attribute]) => (
    <MenuItem key={name} value={name}>{attribute.label}</MenuItem>));

  if (inMenu && !useDesktopLayout) {
    return (
      <MenuItem ref={ref}>
        <Select
          value={sortByAttribute}
          onChange={e => setSortAttribute(e.target.value)}
        >
          {sortItems}
        </Select>
      </MenuItem>
    );
  }

  return (
    <div style={styles.actionElement}>
      {useDesktopLayout && <h3>Sortierung: </h3>}
      <Select
        value={sortByAttribute}
        onChange={e => setSortAttribute(e.target.value)}
        renderValue={useDesktopLayout ? undefined : () => null}
        IconComponent={useDesktopLayout ? undefined : Sort}
        disableUnderline={!useDesktopLayout}
      >
        {sortItems}
      </Select>
    </div>
  );
});

const Alternatives = forwardRef((props, ref) => {
  const {
  // eslint-disable-next-line react/prop-types
    alternative, onNextAlternative, inMenu,
  } = props;

  if (!alternative) {
    return null;
  }

  const button = (
    <Button
      onClick={onNextAlternative}
      variant="outlined"
      style={inMenu ? styles.fullWidth : styles.actionElement}
    >
      {alternative}
    </Button>
  );

  if (inMenu) {
    return (
      <MenuItem ref={ref} onClick={onNextAlternative}>
        {button}
      </MenuItem>
    );
  }

  return button;
});

const CompareToTotal = forwardRef((props, ref) => {
  // eslint-disable-next-line react/prop-types
  const { compareToTotal, setCompareToTotal, inMenu } = props;

  const toggleCompareToTotal = () => setCompareToTotal(!compareToTotal);

  const button = (
    <Button
      onClick={toggleCompareToTotal}
      variant="outlined"
      style={inMenu ? styles.fullWidth : styles.actionElement}
    >
      {compareToTotal ? 'Gesamt' : 'Relativ'}
    </Button>
  );

  if (inMenu) {
    return (
      <MenuItem ref={ref} onClick={toggleCompareToTotal}>
        {compareToTotal ? 'Gesamt' : 'Relativ'}
      </MenuItem>
    );
  }

  return button;
});

const ExportCsv = forwardRef((props, ref) => {
  // eslint-disable-next-line react/prop-types
  const { inMenu, exportCsv, dataIsEmpty } = props;


  const button = (
    <Button
      onClick={exportCsv}
      variant="outlined"
      style={inMenu ? styles.fullWidth : styles.actionElement}
      disabled={dataIsEmpty}
    >
      CSV Export
    </Button>
  );

  if (inMenu) {
    return (
      <MenuItem ref={ref} onClick={exportCsv} disabled={dataIsEmpty}>
        CSV Export
      </MenuItem>
    );
  }

  return button;
});

const ThresholdOnly = forwardRef((props, ref) => {
  // eslint-disable-next-line react/prop-types
  const { inMenu, value, onChange } = props;


  if (inMenu) {
    return (
      <MenuItem ref={ref} onClick={() => onChange(!value)}>
        Nur Lagerstände mit Schwellwert
      </MenuItem>
    );
  }

  return (
    <Button
      onClick={() => onChange(!value)}
      variant="outlined"
      style={styles.actionElement}
    >
      Nur Lagerstände mit Schwellwert
    </Button>
  );
});

const Actions = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    alternative, onNextAlternative,
    sortByAttribute, sortableAttributes, setSortAttribute, useDesktopLayout,
    description, overlayVisible, setOverlayVisible,
    compareToTotal, setCompareToTotal,
    exportCsv, dataIsEmpty,
    showThresholdOnly, setShowThresholdOnly,
  } = props;


  // add to this list when adding a new action type
  const actionCount = [
    alternative, sortByAttribute, description, compareToTotal, exportCsv, setShowThresholdOnly,
  ].filter(x => x).length;
  const multipleActions = actionCount > 1;
  const actionItems = [];

  if (compareToTotal || compareToTotal === false) {
    actionItems.push(
      <CompareToTotal
        key="compareToTotal"
        inMenu={multipleActions && !useDesktopLayout}
        compareToTotal={compareToTotal}
        setCompareToTotal={setCompareToTotal}
      />,
    );
  }

  if (alternative) {
    actionItems.push(
      <Alternatives
        key="alternatives"
        inMenu={multipleActions && !useDesktopLayout}
        alternative={alternative}
        onNextAlternative={onNextAlternative}
      />,
    );
  }

  if (sortByAttribute) {
    actionItems.push(
      <Sortables
        key="sortables"
        inMenu={multipleActions && !useDesktopLayout}
        sortByAttribute={sortByAttribute}
        sortableAttributes={sortableAttributes}
        setSortAttribute={setSortAttribute}
        useDesktopLayout={useDesktopLayout}
      />,
    );
  }

  if (description) {
    actionItems.push(
      <Description
        key="description"
        inMenu={multipleActions && !useDesktopLayout}
        description={description}
        overlayVisible={overlayVisible}
        setOverlayVisible={setOverlayVisible}
      />,
    );
  }

  if (exportCsv) {
    actionItems.push(
      <ExportCsv
        key="exportCsv"
        inMenu={multipleActions && !useDesktopLayout}
        exportCsv={exportCsv}
        dataIsEmpty={dataIsEmpty}
      />,
    );
  }

  if (showThresholdOnly || showThresholdOnly === false) {
    actionItems.push(
      <ThresholdOnly
        key="thresholdOnly"
        inMenu={multipleActions && !useDesktopLayout}
        value={showThresholdOnly}
        onChange={setShowThresholdOnly}
      />,
    );
  }

  if (actionItems.length === 0) {
    return null;
  }

  if (useDesktopLayout || actionItems.length === 1) {
    return (
      <div style={styles.actions}>
        {actionItems}
      </div>
    );
  }

  return (
    <div style={styles.actionItem}>
      <IconButton
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <Icon>more_vert</Icon>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {actionItems}
      </Menu>
    </div>
  );
};

Actions.propTypes = {
  alternative: PropTypes.string,
  compareToTotal: PropTypes.bool,
  description: PropTypes.string,
  overlayVisible: PropTypes.bool.isRequired,
  sortByAttribute: PropTypes.string,
  sortableAttributes: PropTypes.object,
  useDesktopLayout: PropTypes.bool.isRequired,
  dataIsEmpty: PropTypes.bool,
  showThresholdOnly: PropTypes.bool,
  exportCsv: PropTypes.func,
  onNextAlternative: PropTypes.func.isRequired,
  setCompareToTotal: PropTypes.func.isRequired,
  setOverlayVisible: PropTypes.func.isRequired,
  setSortAttribute: PropTypes.func.isRequired,
  setShowThresholdOnly: PropTypes.func.isRequired,
};

Actions.defaultProps = {
  alternative: null,
  compareToTotal: null,
  description: null,
  sortByAttribute: null,
  sortableAttributes: null,
  dataIsEmpty: true,
  exportCsv: null,
  showThresholdOnly: null,
};

export default Actions;
